import { useMemo } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { SelectOption } from '@components/select';
import { showError, showSuccess } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';
import { minutes } from '@utils/milliseconds';

import { useListsScope } from '../integrations-hooks';
import { useIntegrationsApi } from '../integrations-provider';
import type {
  CreateListData,
  DynamicListOption,
  EditListData,
  List,
  ListSyncTestRequestData,
  SyncListCreateData,
  SyncListType,
} from '../integrations-types';

const getListsQueryKey = (templateId?: string) => ['lists', templateId].filter(Boolean);
const getListQueryKey = (id: string, templateId?: string) =>
  ['lists', id, templateId].filter(Boolean);

export const useInvalidateLists = () => {
  const queryClient = useQueryClient();
  const { templateId } = useListsScope();
  const invalidateLists = () => queryClient.invalidateQueries(getListsQueryKey(templateId));
  const invalidateList = (id: string) =>
    queryClient.invalidateQueries(getListQueryKey(id, templateId));

  return { invalidateLists, invalidateList };
};

export const useList = (
  id: string,
  options?: {
    onSuccess?: (list: List) => void;
    staleTime?: number;
    cacheTime?: number;
  },
) => {
  const api = useIntegrationsApi();
  const { templateId } = useListsScope();
  return useQuery({
    queryKey: getListQueryKey(id, templateId),
    queryFn: () => api.loadList(id, { templateId }),
    enabled: Boolean(id),
    ...options,
  });
};

export const filterListOptions = (
  listOptions: List['options'],
  excludedOptions: string[] = [],
): Array<SelectOption & { altValue?: string }> => {
  return listOptions
    .filter((option) => {
      return !excludedOptions.includes(option.value) && !excludedOptions.includes(option.label);
    })
    .map((option) => {
      return { value: option.value, label: option.label, altValue: option.shortId };
    });
};

export const useListOptions = (
  optionsFromListId: string | null | undefined,
  excludedOptions?: string[],
): Array<SelectOption & { altValue?: string }> => {
  const listQuery = useList(optionsFromListId ?? '', {
    staleTime: minutes(5),
  });
  return useMemo(() => {
    if (!optionsFromListId || !listQuery.data) {
      return [];
    }
    return filterListOptions(listQuery.data.options, excludedOptions);
  }, [optionsFromListId, excludedOptions, listQuery.data]);
};

export const useCreateList = (options?: {
  onSuccess?: () => void;
  onError?: (error: any) => void;
}) => {
  const { t } = useScopedTranslate(listHooksLocale);
  const api = useIntegrationsApi();
  const { templateId } = useListsScope();
  const { invalidateLists } = useInvalidateLists();
  return useMutation({
    mutationFn: (data: CreateListData) => api.createList(data, { templateId }),
    onSuccess: () => {
      invalidateLists();
      showSuccess(t('listCreated'));
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
};

export const useEditList = (options?: {
  onSuccess?: () => void;
  onError?: (error: any) => void;
}) => {
  const { t } = useScopedTranslate(listHooksLocale);
  const api = useIntegrationsApi();
  const { templateId } = useListsScope();
  const { invalidateLists, invalidateList } = useInvalidateLists();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: EditListData }) =>
      api.editList(id, data, { templateId }),
    onSuccess: (_data, variables) => {
      invalidateLists();
      invalidateList(variables.id);
      showSuccess(t('listUpdated'));
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
};

export const useDeleteList = (options?: {
  onSuccess?: () => void;
  onError?: (error: any) => void;
}) => {
  const api = useIntegrationsApi();
  const { templateId } = useListsScope();
  const { invalidateLists } = useInvalidateLists();
  return useMutation({
    mutationFn: (id: string) => api.deleteList(id, { templateId }),
    onSuccess: () => {
      invalidateLists();
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
};

export const useCreateSyncList = (options?: {
  onSuccess?: () => void;
  onError?: (error: any) => void;
}) => {
  const api = useIntegrationsApi();
  const { templateId } = useListsScope();
  const { invalidateLists } = useInvalidateLists();
  return useMutation({
    mutationFn: (data: SyncListCreateData) => api.createSyncList(data, { templateId }),
    onSuccess: () => {
      invalidateLists();
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
};

export const usePatchSyncList = (options?: {
  onSuccess?: () => void;
  onError?: (error: any) => void;
}) => {
  const api = useIntegrationsApi();
  const { templateId } = useListsScope();
  const { invalidateLists } = useInvalidateLists();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<SyncListType> }) =>
      api.patchSyncList(id, data, { templateId }),
    onSuccess: () => {
      invalidateLists();
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
};

export const useSyncList = (options?: { onSuccess?: () => void; onError?: () => void }) => {
  const api = useIntegrationsApi();
  const { templateId } = useListsScope();
  const { invalidateLists } = useInvalidateLists();
  return useMutation({
    mutationFn: (id: string) => api.syncList(id, { templateId }),
    onSuccess: () => {
      invalidateLists();
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
};

export const useSyncToManualList = (options?: {
  onSuccess?: () => void;
  onError?: (error: any) => void;
}) => {
  const api = useIntegrationsApi();
  const { templateId } = useListsScope();
  const { invalidateLists } = useInvalidateLists();
  return useMutation({
    mutationFn: (id: string) => api.syncToManualList(id, { templateId }),
    onSuccess: () => {
      invalidateLists();
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
};

export const useListSyncTestRequest = (integrationId: string) => {
  const api = useIntegrationsApi();
  return useMutation({
    mutationFn: (data: ListSyncTestRequestData) => api.listSyncTestRequest(integrationId, data),
    onError: (error: any) => {
      const message = error?.response?.data?.detail ?? error?.response?.data?.error;
      if (message) {
        showError(message);
      }
    },
  });
};

export const useDynamicListOptions = (
  listShortId: string | undefined,
  parameters: Record<string, string>,
  enabled: boolean,
): { options: DynamicListOption[]; isLoading: boolean; isFetched: boolean } => {
  const { t } = useScopedTranslate(listHooksLocale);
  const api = useIntegrationsApi();
  const { templateId } = useListsScope();
  const query = useQuery({
    queryKey: ['dynamic-list-options', templateId, listShortId, parameters],
    queryFn: () => api.dynamicListOptions(listShortId!, parameters, { templateId }),
    onError: () => showError(t('couldNotFetchOptions')),
    enabled: enabled && Boolean(listShortId),
    staleTime: 300_000,
  });
  return {
    options: enabled ? (query.data ?? []) : [],
    isLoading: query.isFetching,
    isFetched: enabled ? query.isFetched : false,
  };
};

const listHooksLocale = {
  en: {
    listCreated: 'List created',
    listUpdated: 'List Updated',
    couldNotFetchOptions: "Couldn't fetch options",
  },
};
